<template>
  <div class="tranzila-wrap" dir="rtl">
    <div
      v-if="successMessage"
      class="tranzila-banner tranzila-banner--success"
      role="alert"
      aria-live="assertive"
    >{{ successMessage }}</div>

    <div
      v-if="createError"
      class="tranzila-banner tranzila-banner--error"
      role="alert"
      aria-live="assertive"
    >{{ createError }}</div>

    <div v-if="creating" class="tranzila-loading" role="status">מכין את התשלום...</div>

    <div v-else-if="resuming" class="tranzila-loading" role="status">מאמת את התשלום...</div>

    <template v-else-if="iframeSrc">
      <p v-if="!sum" class="tranzila-notice"><strong>על כפתור החיוב רשום 5 שקלים</strong> אך שימו לב פעולה זאת לא מחייבת את הכרטיס</p>

      <iframe
        id="tranzila-frame"
        allowpaymentrequest="true"
        name="tranzila"
        :src="iframeSrc"
        title="טופס תשלום Tranzila"
      ></iframe>
    </template>
  </div>
</template>

<script>
export default {
  props: {
    user_id: { type: String, default: '' },
    sum: { type: Number, default: 0 },
    plan_id: { type: Number, default: 0 },
    uid: { type: Number, default: 0 },
    // Pending-signup path (new registration/payment gate): identifies a staged,
    // not-yet-created account. When set, create_payment/check_payment are called
    // with { pending_token } instead of the authenticated user's session, and no
    // account/user_token exists until check_payment reports done:true.
    pending_token: { type: String, default: '' },
    // Resume-poll-only mode: pending_token survived a page reload (e.g. a
    // 3-D Secure redirect bouncing the top-level page away from the Tranzila
    // iframe and back). Never re-run create_payment in this case — that would
    // create a second payment attempt for one that may already be in flight
    // or have succeeded server-side. Just resume polling check_payment.
    resume: { type: Boolean, default: false },
  },
  computed: {
    isPending() {
      return !!this.pending_token;
    },
    iframeSrc() {
      // Plan payment (authenticated or pending-signup): route through the payments
      // controller (do_redirect) so the charge flows through the proven
      // notify -> is_paid -> notify_do pipeline. The API returns the absolute
      // redirect_url from create_payment.
      if (this.isPending || this.plan_id > 0) return this.redirectUrl;

      // Card-only flow (no plan, no payment record): tokenize the card, no charge.
      //   tranmode=K -> create token without charging; cred_type=1 standard credit card.
      //   hidesum=1 is valid with K and hides the placeholder sum.
      const uid = this.resolvedUserId || this.user_id;
      if (!uid) return '';
      const notifyBase = 'https://api.tov.tv/api/payment_res';
      const successUrl = `https://api.tov.tv/api/payment_success?user_id=${encodeURIComponent(uid)}`;
      return `https://direct.tranzila.com/tovtv/iframenew.php?sum=5&cred_type=1&currency=1&tranmode=K&hidesum=1&lang=il&user_id=${uid}&notify_url_address=${notifyBase}&success_url_address=${encodeURIComponent(successUrl)}`;
    },
  },
  data() {
    return {
      is_check_payment: true,
      sys_interval: null,
      successMessage: '',
      resolvedUserId: '',
      redirectUrl: '',
      creating: false,
      createError: '',
      resuming: false,
    };
  },
  mounted() {
    // Resuming a pending_token that survived a reload: skip create_payment
    // entirely and just resume polling.
    if (this.isPending && this.resume) {
      this.resuming = true;
      this.resolvedUserId = this.pending_token;
      this.startPolling();
      return;
    }

    // Pending-signup path: create the payment record against the staged
    // (not-yet-created) account. Authenticated plan payment: same, but keyed
    // off the logged-in session instead of a pending_token. Card-only flow
    // (no plan, already logged in) uses the user_id/uid props directly.
    if (this.isPending || this.plan_id > 0) {
      this.createPayment();
    } else {
      this.resolvedUserId = this.user_id;
      this.startPolling();
    }
  },
  methods: {
    createPayment() {
      this.creating = true;
      this.createError = '';
      this.api_call({
        url: 'create_payment',
        data: this.isPending
          ? { pending_token: this.pending_token }
          : { plan_id: this.plan_id },
        onComplete: (resp) => {
          this.creating = false;
          const d = resp && resp.data && resp.data.data;
          if (this.isPending) {
            if (d && d.success && d.redirect_url && d.poll_token) {
              this.resolvedUserId = d.poll_token; // === pending_token, used for check_payment polling
              this.redirectUrl = /^https?:\/\//.test(d.redirect_url)
                ? d.redirect_url
                : `https://api.tov.tv${d.redirect_url}`;
              this.startPolling();
              return;
            }
          } else if (d && d.success && d.user_id && d.redirect_url) {
            this.resolvedUserId = d.user_id;   // token for check_payment polling
            // Absolutize: older API may return a relative /payments/... path
            this.redirectUrl = /^https?:\/\//.test(d.redirect_url)
              ? d.redirect_url
              : `https://api.tov.tv${d.redirect_url}`;
            this.startPolling();
            return;
          }
          this.createError = 'אירעה שגיאה ביצירת התשלום. נסה/י שוב.';
        },
      }).catch(() => {
        this.creating = false;
        this.createError = 'אירעה שגיאה בחיבור לשרת. נסה/י שוב.';
      });
    },
    startPolling() {
      this.is_check_payment = true;
      if (this.sys_interval) clearInterval(this.sys_interval);
      this.sys_interval = setInterval(this.check_payment, 3000);
    },
    check_payment() {
      if (!this.is_check_payment) return;

      if (this.isPending) {
        this.api_call({
          url: 'check_payment',
          data: { pending_token: this.pending_token },
          onComplete: (resp) => {
            const d = resp && resp.data && resp.data.data;
            if (d && d.success === false && d.error === 'not_found') {
              this.is_check_payment = false;
              this.createError = 'פג תוקף הבקשה. נסה/י שוב.';
              this.$emit('payment_done', false);
              return;
            }
            if (d && d.success && d.done === true && d.user_token) {
              this.successMessage = 'התשלום התקבל בהצלחה!';
              this.is_check_payment = false;
              // Only here does an account/session actually exist — hand the
              // fresh user_token up to the caller, which stores it.
              this.$emit('payment_done', true, d.user_token, d.uid);
            }
            // success:true, done:false -> keep polling, no-op.
          },
        });
        return;
      }

      const uid = this.resolvedUserId || this.user_id;
      if (!uid) return;
      const mode = this.plan_id > 0 ? 'plan' : 'card';
      this.api_call({
        url: 'check_payment',
        data: {
          user_id: uid,
          mode,
          plan_id: this.plan_id > 0 ? this.plan_id : 0,
        },
        onComplete: (data) => {
          if (data.data && data.data.data && data.data.data === true) {
            this.successMessage = 'הפרטים עודכנו בהצלחה!';
            this.is_check_payment = false;
            this.$emit('payment_done', true);
          }
        },
      });
    },
  },
  beforeDestroy() {
    clearInterval(this.sys_interval);
  },
  destroyed() {
    clearInterval(this.sys_interval);
  },
};
</script>

<style scoped lang="scss">
.tranzila-wrap {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  font-family: var(--font-hebrew);
}

.tranzila-notice {
  font-size: 13px;
  color: var(--color-textMuted);
  text-align: center;
  margin: 0;
  line-height: 1.5;
}

.tranzila-banner {
  padding: var(--space-3) var(--space-4);
  border-radius: var(--radius-sm);
  font-size: 15px;
  line-height: 1.5;
}

.tranzila-banner--success {
  background-color: rgba(31, 174, 107, 0.12);
  border: 1px solid rgba(31, 174, 107, 0.33);
  color: var(--color-success);
}

.tranzila-banner--error {
  background-color: rgba(224, 65, 65, 0.12);
  border: 1px solid rgba(224, 65, 65, 0.33);
  color: var(--color-danger);
}

.tranzila-loading {
  padding: var(--space-6);
  text-align: center;
  font-size: 15px;
  color: var(--color-textMuted);
}

iframe {
  border: none;
  outline: none;
  border-radius: var(--radius-md);
  width: 100%;
  display: block;
  height: var(--tranzila-frame-height, 72vh);
  min-height: 600px;
}

@media (max-width: 900px) {
  iframe {
    height: 72vh;
    min-height: 520px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .tranzila-banner {
    transition: none;
  }
}
</style>
